import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CreditCard, Lock, AlertCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';

export default function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentOrder, clearCart } = useApp();
  const orderId = location.state?.orderId || currentOrder?.id;
  const total = location.state?.total || currentOrder?.total || 0;
  const orderType = currentOrder?.type || 'delivery';

  const [cardDetails, setCardDetails] = useState({ number: '', name: '', expiry: '', cvv: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [restaurant, setRestaurant] = useState(null);

useEffect(() => {
  let tag = document.querySelector('meta[name="robots"]');

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'robots');
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', 'noindex, nofollow');

  return () => {
    tag.setAttribute('content', 'index, follow');
  };
}, []);

  useEffect(() => {
  api.getRestaurant()
    .then(setRestaurant)
    .catch(() => {});
}, []);

useEffect(() => {
  if (restaurant?.name) {
    document.title = `Payment - ${restaurant.name}`;
  } else {
    document.title = 'Payment';
  }
}, [restaurant]);

  const formatCardNumber = (v) => {
    const s = v.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const parts = [];
    for (let i = 0, len = s.length; i < len; i += 4) parts.push(s.substring(i, i + 4));
    return parts.length ? parts.join(' ') : v;
  };

  const formatExpiry = (v) => {
    const s = v.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (s.length >= 2) return s.substring(0, 2) + '/' + s.substring(2, 4);
    return s;
  };

  const handlePayment = async () => {
    if (!cardDetails.number || !cardDetails.name || !cardDetails.expiry || !cardDetails.cvv) {
      setError('Please fill in all payment details');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Create payment intent
      const intent = await api.createPaymentIntent(orderId, total);

      // Simulate Stripe payment confirmation (mock)
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Confirm payment on backend
      await api.confirmPayment(orderId, intent.clientSecret || 'mock_payment_id');

      clearCart();
      navigate(`/order-status/${orderId}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (

    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/checkout')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-lg font-bold text-gray-900">Payment</h1>
          <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
            <Lock className="w-3.5 h-3.5" /><span>Secured by Stripe</span>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Amount */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex justify-between items-center">
            <span className="text-amber-800 font-medium text-sm">Amount to pay</span>
            <span className="text-amber-900 font-bold text-2xl">£{parseFloat(total).toFixed(2)}</span>
          </div>
          <p className="text-amber-700 text-xs mt-1 capitalize">{orderType} order</p>
        </div>

        {/* Card Details */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-amber-600" />
            <h3 className="font-semibold text-gray-900">Card Details</h3>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Card Number</label>
              <input type="text" placeholder="1234 5678 9012 3456" maxLength={19}
                value={cardDetails.number}
                onChange={e => setCardDetails(p => ({ ...p, number: formatCardNumber(e.target.value) }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors font-mono tracking-wider" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Cardholder Name</label>
              <input type="text" placeholder="Name as it appears on card"
                value={cardDetails.name}
                onChange={e => setCardDetails(p => ({ ...p, name: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Expiry Date</label>
                <input type="text" placeholder="MM/YY" maxLength={5}
                  value={cardDetails.expiry}
                  onChange={e => setCardDetails(p => ({ ...p, expiry: formatExpiry(e.target.value) }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors font-mono" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">CVV</label>
                <input type="password" placeholder="•••" maxLength={4}
                  value={cardDetails.cvv}
                  onChange={e => setCardDetails(p => ({ ...p, cvv: e.target.value.replace(/\D/g, '') }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors font-mono" />
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl p-3">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div className="flex items-center gap-3 justify-center py-1">
          {['VISA', 'MC', 'AMEX'].map(card => (
            <div key={card} className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs font-bold text-gray-500">{card}</div>
          ))}
        </div>

        <button onClick={handlePayment} disabled={loading}
          className="w-full bg-gray-900 text-white py-4 rounded-2xl font-bold text-base hover:bg-amber-700 transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? (
            <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Processing payment...</>
          ) : (
            <><Lock className="w-4 h-4" />Pay £{parseFloat(total).toFixed(2)} securely</>
          )}
        </button>

        <p className="text-center text-xs text-gray-400">
          By placing your order, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
